import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { Button, Icon, Tag, Card, Callout } from '@blueprintjs/core';

import { TAB_ACTIVE_JOBS } from '../index';
import ReportSelector from '../shared/report-selector';
import DistributionSelector from '../shared/distribution-selector';
import ScheduleSelector from '../shared/schedule-selector';
import * as Selectors from '../../../selectors';
import * as API from '../../../services/api';
import { CAN_MANAGE_REPORTS } from '../../../constants/permissions';
import {
  SELECTED_REPORT_DEFAULT,
  SELECTED_DISTRIBUTION_DEFAULT,
  SELECTED_SCHEDULE_DEFAULT,
} from '../helpers/consts';
import { validate } from '../helpers/validate';
import { getEmailsAsArray } from '../helpers/getEmailsAsArray';

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  margin-top: 1.5em;
`;
const StyledIcon = styled(Icon)`
  margin: 0 0.5em 0 0;
`;
const Row = styled(Card)`
  display: flex;
  width: 100%;
  margin: 0 0 1em 0;
`;
const ButtonRow = styled.div``;
const NumberCol = styled.div`
  width: 10%;
`;
const FormCol = styled.div`
  width: 90%;
`;
const ColTitle = styled.div`
  display: flex;
  margin: 0 0 1em 0;
  border-bottom: 1px solid #ced9e0;
`;
const StyledCallout = styled(Callout)`
  margin: 1em 0;
`;

const EditJobTab = ({
  reports,
  setCurrentTab,
  user,
  canManageReports,
  selectedJob,
  loadJobs,
}) => {
  const [selectedReport, setSelectedReport] = useState(
    selectedJob || SELECTED_REPORT_DEFAULT
  );
  const [selectedDistribution, setSelectedDistribution] = useState(
    selectedReport ? selectedReport.distribution : SELECTED_DISTRIBUTION_DEFAULT
  );
  const [selectedSchedule, setSelectedSchedule] = useState(
    selectedReport ? selectedReport.schedule : SELECTED_SCHEDULE_DEFAULT
  );
  const [restrictToLocations, setRestrictToLocations] = useState(
    selectedReport &&
      selectedReport.dashboardReportToImage &&
      Array.isArray(selectedReport.dashboardReportToImage.restrictToLocations)
      ? selectedReport.dashboardReportToImage.restrictToLocations
      : []
  );
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState({});

  useEffect(() => {
    if (
      selectedJob &&
      selectedJob.distribution &&
      selectedJob.schedule &&
      selectedJob.dashboardReportToImage
    ) {
      setSelectedReport(
        reports.find(
          (report) =>
            report.id === selectedJob.dashboardReportToImage.dashboardReportId
        )
      );
      setSelectedDistribution(selectedJob.distribution);
      setSelectedSchedule(selectedJob.schedule);
      setRestrictToLocations(
        Array.isArray(selectedJob.dashboardReportToImage.restrictToLocations)
          ? selectedJob.dashboardReportToImage.restrictToLocations
          : []
      );
    }
  }, [reports, selectedJob]);

  const ErrorMessage = ({ error }) => {
    if (error) return <StyledCallout intent="warning">{error}</StyledCallout>;
    return null;
  };

  const updateJob = async () => {
    setApiResponse({});
    if (
      !validate(
        setErrors,
        selectedReport,
        selectedDistribution,
        selectedSchedule
      )
    )
      return false;

    const payload = {
      dashboardReportToImage: {
        dashboardReportId: selectedReport.id,
      },
      schedule: selectedSchedule,
      distribution: {
        sendType: selectedDistribution.sendType,
        toEmails: getEmailsAsArray(selectedDistribution),
      },
      jobType: 'dashboardReportToImage',
      impersonationAuth0Id: selectedJob.impersonationAuth0Id,
    };

    if (restrictToLocations && restrictToLocations.length > 0) {
      payload.dashboardReportToImage.restrictToLocations = restrictToLocations;
    }

    setLoading(true);
    try {
      await API.scheduledReportJobs.update(selectedJob.id, payload);

      setSelectedReport(SELECTED_REPORT_DEFAULT);
      setSelectedDistribution(SELECTED_DISTRIBUTION_DEFAULT);
      setSelectedSchedule(SELECTED_SCHEDULE_DEFAULT);

      setApiResponse({
        intent: 'success',
        message: 'Report updated successfully',
      });

      setCurrentTab(TAB_ACTIVE_JOBS);
    } catch (e) {
      console.error(
        'Got an API error when trying to update the scheduled report job',
        e
      );
      setApiResponse({
        intent: 'warning',
        message: 'Sorry, there was an error - report not updated',
      });
    }
    setLoading(false);
  };

  return (
    <div>
      <Row>
        <NumberCol>
          <Tag large minimal round intent="primary">
            1
          </Tag>
        </NumberCol>
        <FormCol>
          <ColTitle>
            <StyledIcon intent="primary" icon="clipboard" />
            <h5 className="bp3-heading">Choose which report to send</h5>
          </ColTitle>
          <ErrorMessage error={errors.report} />
          <ReportSelector
            reports={reports}
            selectedReport={selectedReport}
            setSelectedReport={setSelectedReport}
            restrictToLocations={restrictToLocations}
            setRestrictToLocations={setRestrictToLocations}
          />
        </FormCol>
      </Row>
      <Row>
        <NumberCol>
          <Tag large minimal round intent="primary">
            2
          </Tag>
        </NumberCol>
        <FormCol>
          <ColTitle>
            <StyledIcon intent="primary" icon="envelope" />
            <h5 className="bp3-heading">
              Choose who should get sent the report
            </h5>
          </ColTitle>
          <ErrorMessage error={errors.distribution} />
          <DistributionSelector
            selectedDistribution={selectedDistribution}
            setSelectedDistribution={setSelectedDistribution}
          />
        </FormCol>
      </Row>
      <Row>
        <NumberCol>
          <Tag large minimal round intent="primary">
            3
          </Tag>
        </NumberCol>
        <FormCol>
          <ColTitle>
            <StyledIcon intent="primary" icon="timeline-events" />
            <h5 className="bp3-heading">
              Set a schedule for this email report
            </h5>
          </ColTitle>
          <ErrorMessage error={errors.schedule} />
          <ScheduleSelector
            selectedSchedule={selectedSchedule}
            setSelectedSchedule={setSelectedSchedule}
          />
        </FormCol>
      </Row>
      <ButtonRow>
        {!isEmpty(apiResponse) && (
          <Callout intent={apiResponse.intent}>{apiResponse.message}</Callout>
        )}
        <ButtonContainer>
          <Button
            icon="arrow-left"
            onClick={(e) => setCurrentTab(TAB_ACTIVE_JOBS)}
          >
            Back
          </Button>
          {!canManageReports && (
            <Callout intent="warning">
              You do not have permission to edit email reports
            </Callout>
          )}
          <Button
            intent="primary"
            onClick={updateJob}
            loading={loading}
            disabled={loading || !canManageReports}
          >
            Save
          </Button>
        </ButtonContainer>
      </ButtonRow>
    </div>
  );
};

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    user: Selectors.getUserModel(state),
    locations: Selectors.getLocations(state),
    canManageReports: Selectors.userHasPermission(CAN_MANAGE_REPORTS, state),
  };
}

export default connect(mapStateToProps)(EditJobTab);
